// pages/SpeechIsolation.jsx
import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Upload,
  Mic,
  Download,
  Play,
  Pause,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  X,
  Volume2,
  FileAudio,
  Sparkles,
  ArrowRight,
  Wand2,
  SlidersHorizontal,
} from "lucide-react";
import api from "../../services/api";

// ─── Waveform Visualiser ──────────────────────────────────────────────────
const WaveformBars = ({ active, count = 36, color = "#7c3aed" }) => {
  const heights = useRef(
    Array.from(
      { length: count },
      (_, i) => 20 + Math.sin((i / count) * Math.PI) * 60,
    ),
  );
  return (
    <div className="flex items-center gap-[2px] h-8">
      {heights.current.map((h, i) => (
        <div
          key={i}
          className="flex-shrink-0 rounded-full"
          style={{
            width: "3px",
            height: `${h}%`,
            background: color,
            opacity: active ? 0.85 : 0.3,
            animation: active
              ? `wavePulse 1s ease-in-out ${i * 35}ms infinite alternate`
              : "none",
          }}
        />
      ))}
      <style>{`@keyframes wavePulse { 0%{transform:scaleY(0.3)} 100%{transform:scaleY(1)} }`}</style>
    </div>
  );
};

// ─── Audio Player Card ────────────────────────────────────────────────────
const AudioPlayerCard = ({ label, url, icon: Icon, color, sublabel }) => {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
  }, [url]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) audio.play().catch(() => setPlaying(false));
    else audio.pause();
  }, [playing]);

  const fmt = (s) =>
    `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="rounded-2xl p-4 space-y-3"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${color}1a`, border: `1px solid ${color}30` }}
        >
          <Icon size={14} style={{ color }} />
        </div>
        <div>
          <p className="text-xs font-semibold text-white leading-none">
            {label}
          </p>
          {sublabel && (
            <p className="text-[10px] text-gray-600 mt-0.5">{sublabel}</p>
          )}
        </div>
      </div>
      <WaveformBars active={playing} count={32} color={color} />
      <div
        className="relative h-1.5 rounded-full cursor-pointer overflow-hidden"
        style={{ background: "rgba(255,255,255,0.07)" }}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          if (audioRef.current && duration)
            audioRef.current.currentTime =
              ((e.clientX - r.left) / r.width) * duration;
        }}
      >
        <div
          className="absolute left-0 top-0 bottom-0 rounded-full"
          style={{
            width: `${progress}%`,
            background: color,
            transition: "width 0.1s linear",
          }}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono text-gray-600">
          {fmt(currentTime)}
        </span>
        <button
          onClick={() => setPlaying((p) => !p)}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
          style={{ background: `${color}22`, border: `1px solid ${color}40` }}
        >
          {playing ? (
            <Pause size={12} style={{ color }} />
          ) : (
            <Play size={12} style={{ color }} />
          )}
        </button>
        <span className="text-[10px] font-mono text-gray-600">
          {fmt(duration)}
        </span>
      </div>
      {url && (
        <audio
          ref={audioRef}
          src={url}
          onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.target.duration)}
          onEnded={() => setPlaying(false)}
        />
      )}
    </div>
  );
};

// ─── Strength Selector ────────────────────────────────────────────────────
const STRENGTHS = [
  {
    key: "gentle",
    label: "Gentle",
    desc: "Light touch — preserves naturalness",
  },
  {
    key: "medium",
    label: "Medium",
    desc: "Balanced — good for most recordings",
  },
  {
    key: "aggressive",
    label: "Aggressive",
    desc: "Max removal — may affect voice tone",
  },
];

const StrengthSelector = ({ value, onChange }) => (
  <div>
    <p className="text-[10px] font-bold text-gray-600 uppercase tracking-[0.1em] mb-2.5 flex items-center gap-1.5">
      <SlidersHorizontal size={10} /> Noise Removal Strength
    </p>
    <div className="flex gap-2">
      {STRENGTHS.map((s) => (
        <button
          key={s.key}
          onClick={() => onChange(s.key)}
          className="flex-1 flex flex-col items-start gap-1 p-3 rounded-xl transition-all text-left"
          style={{
            background:
              value === s.key
                ? "rgba(124,58,237,0.12)"
                : "rgba(255,255,255,0.02)",
            border:
              value === s.key
                ? "1px solid rgba(124,58,237,0.4)"
                : "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <span
            className="text-xs font-semibold"
            style={{ color: value === s.key ? "#c4b5fd" : "#6b7280" }}
          >
            {s.label}
          </span>
          <span className="text-[10px] text-gray-700 leading-snug">
            {s.desc}
          </span>
        </button>
      ))}
    </div>
  </div>
);

// ─── Drop Zone ────────────────────────────────────────────────────────────
const DropZone = ({ onFile }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) onFile(f);
      }}
      className="flex flex-col items-center justify-center rounded-2xl cursor-pointer transition-all duration-300 py-10 px-6 text-center"
      style={{
        background: dragging
          ? "rgba(124,58,237,0.08)"
          : "rgba(255,255,255,0.02)",
        border: dragging
          ? "2px dashed rgba(124,58,237,0.6)"
          : "2px dashed rgba(255,255,255,0.08)",
      }}
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
        style={{
          background: dragging
            ? "rgba(124,58,237,0.15)"
            : "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow: dragging ? "0 0 40px rgba(124,58,237,0.2)" : "none",
          transition: "all 0.3s ease",
        }}
      >
        <Upload
          size={22}
          className={dragging ? "text-violet-400" : "text-gray-600"}
        />
      </div>
      <p className="text-sm font-semibold text-gray-400 mb-1">
        {dragging ? "Drop it here" : "Drop audio file or click to browse"}
      </p>
      <p className="text-xs text-gray-700">MP3, WAV, OGG, M4A · Up to 200 MB</p>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        onChange={(e) => {
          const f = e.target.files[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
        className="hidden"
      />
    </div>
  );
};

// ─── Progress Bar ─────────────────────────────────────────────────────────
const ProgressBar = ({ progress, label }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <p className="text-xs text-gray-400 font-medium">{label}</p>
      <span className="text-xs font-mono text-violet-400 tabular-nums">
        {Math.round(progress)}%
      </span>
    </div>
    <div
      className="rounded-full overflow-hidden"
      style={{ background: "rgba(255,255,255,0.05)", height: "6px" }}
    >
      <div
        className="h-full rounded-full"
        style={{
          width: `${progress}%`,
          background: "linear-gradient(90deg, #7c3aed, #a855f7)",
          transition: "width 0.4s ease",
          boxShadow: "0 0 8px rgba(124,58,237,0.5)",
        }}
      />
    </div>
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────
const SpeechIsolation = () => {
  const [stage, setStage] = useState("idle");
  const [file, setFile] = useState(null);
  const [strength, setStrength] = useState("medium");
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [originalPreviewUrl, setOriginalPreviewUrl] = useState(null);
  const timerRef = useRef(null);

  const clearTimers = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const simulateProgress = useCallback(() => {
    const steps = [
      { pct: 10, label: "Uploading audio…", delay: 400 },
      { pct: 28, label: "Analysing frequencies…", delay: 700 },
      { pct: 50, label: "Applying noise filters…", delay: 900 },
      { pct: 68, label: "Running frequency denoiser…", delay: 800 },
      { pct: 82, label: "Post-processing audio…", delay: 700 },
      { pct: 92, label: "Encoding output file…", delay: 600 },
      { pct: 97, label: "Uploading to storage…", delay: 500 },
    ];
    let i = 0;
    const tick = () => {
      if (i >= steps.length) return;
      const { pct, label, delay } = steps[i++];
      timerRef.current = setTimeout(() => {
        setProgress(pct);
        setProgressLabel(label);
        tick();
      }, delay);
    };
    tick();
  }, []);

  const handleFile = (f) => {
    setFile(f);
    setStage("uploaded");
    setError(null);
    setResult(null);
    if (originalPreviewUrl) URL.revokeObjectURL(originalPreviewUrl);
    setOriginalPreviewUrl(URL.createObjectURL(f));
  };

  const handleReset = () => {
    clearTimers();
    if (originalPreviewUrl) URL.revokeObjectURL(originalPreviewUrl);
    setStage("idle");
    setFile(null);
    setProgress(0);
    setProgressLabel("");
    setResult(null);
    setError(null);
    setOriginalPreviewUrl(null);
  };

  const handleIsolate = async () => {
    if (!file) return;
    setStage("processing");
    setProgress(0);
    setError(null);
    simulateProgress();
    try {
      const formData = new FormData();
      formData.append("audio", file);
      formData.append("strength", strength);
      const res = await api.isolateSpeech(formData);
      clearTimers();
      setProgress(100);
      setProgressLabel("Done!");
      setTimeout(() => {
        setResult({
          originalUrl: res.originalUrl || originalPreviewUrl,
          isolatedUrl: res.isolatedUrl,
          strength: res.strength,
          filename: file.name,
        });
        setStage("done");
      }, 400);
    } catch (err) {
      clearTimers();
      setError(
        err?.response?.data?.message || err.message || "Processing failed.",
      );
      setStage("error");
    }
  };

  const fmtSize = (b) =>
    b < 1024 * 1024
      ? `${(b / 1024).toFixed(1)} KB`
      : `${(b / (1024 * 1024)).toFixed(1)} MB`;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: "linear-gradient(180deg, #0a0c12 0%, #07090f 100%)",
        color: "white",
      }}
    >
      {/* Nav */}
      <div
        className="flex-shrink-0 flex items-center justify-between px-5 h-12"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          background: "rgba(0,0,0,0.3)",
          backdropFilter: "blur(12px)",
        }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-6 h-6 rounded-lg flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #7c3aed, #6d28d9)" }}
          >
            <Wand2 size={12} className="text-white" />
          </div>
          <span className="text-sm font-bold text-white tracking-tight">
            Speech Isolation
          </span>
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
            style={{
              background: "rgba(124,58,237,0.2)",
              color: "#a78bfa",
              border: "1px solid rgba(124,58,237,0.3)",
            }}
          >
            Beta
          </span>
        </div>
        {/* <span className="text-[10px] text-gray-700">
          Noise reduction · zero cost
        </span> */}
      </div>

      {/* Body */}
      <div className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-lg space-y-6">
          {stage === "idle" && (
            <div className="text-center space-y-2 pb-2">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold mb-3"
                style={{
                  background: "rgba(124,58,237,0.1)",
                  border: "1px solid rgba(124,58,237,0.2)",
                  color: "#a78bfa",
                }}
              >
                <Sparkles size={11} /> AI-Powered Noise Removal
              </div>
              <h1 className="text-2xl font-bold text-white tracking-tight">
                Remove background noise
              </h1>
              <p className="text-sm text-gray-500 max-w-sm mx-auto leading-relaxed">
                Upload a voice recording and strip out hiss, hum, fan noise, and
                room ambience — leaving clean, clear speech.
              </p>
            </div>
          )}

          {/* Card */}
          <div
            className="rounded-3xl overflow-hidden shadow-2xl"
            style={{
              background: "linear-gradient(180deg, #0f1117 0%, #0a0c12 100%)",
              border: "1px solid rgba(255,255,255,0.07)",
              boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
            }}
          >
            <div className="px-6 py-6">
              {/* ── IDLE ── */}
              {stage === "idle" && (
                <div className="space-y-5">
                  <StrengthSelector value={strength} onChange={setStrength} />
                  <DropZone onFile={handleFile} />
                </div>
              )}

              {/* ── UPLOADED ── */}
              {stage === "uploaded" && (
                <div className="space-y-4">
                  <StrengthSelector value={strength} onChange={setStrength} />
                  <div
                    className="flex items-center gap-3 rounded-2xl px-4 py-3"
                    style={{
                      background: "rgba(124,58,237,0.07)",
                      border: "1px solid rgba(124,58,237,0.2)",
                    }}
                  >
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "rgba(124,58,237,0.15)" }}
                    >
                      <FileAudio size={16} className="text-violet-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {file.name}
                      </p>
                      <p className="text-[10px] text-gray-600 mt-0.5">
                        {fmtSize(file.size)}
                      </p>
                    </div>
                    <button
                      onClick={handleReset}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-600 hover:text-white hover:bg-white/10 transition-all"
                    >
                      <X size={13} />
                    </button>
                  </div>
                  {originalPreviewUrl && (
                    <AudioPlayerCard
                      label="Original Audio"
                      url={originalPreviewUrl}
                      icon={Volume2}
                      color="#6b7280"
                      sublabel="Your uploaded file"
                    />
                  )}
                  <button
                    onClick={handleIsolate}
                    className="w-full flex items-center justify-center gap-2.5 py-3 rounded-xl text-sm font-bold text-white transition-all"
                    style={{
                      background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                      boxShadow: "0 4px 20px rgba(124,58,237,0.35)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 6px 28px rgba(124,58,237,0.5)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 4px 20px rgba(124,58,237,0.35)";
                    }}
                  >
                    <Wand2 size={15} /> Remove Background Noise{" "}
                    <ArrowRight size={14} className="opacity-60" />
                  </button>
                </div>
              )}

              {/* ── PROCESSING ── */}
              {stage === "processing" && (
                <div className="py-6 space-y-6">
                  <div className="text-center space-y-1">
                    <p className="text-sm font-semibold text-white">
                      Processing your audio
                    </p>
                    <p className="text-[11px] text-gray-600 truncate">
                      {file?.name}
                    </p>
                  </div>
                  <ProgressBar progress={progress} label={progressLabel} />
                  <p className="text-center text-[10px] text-gray-700">
                    Noise filters running · typically under 30 seconds
                  </p>
                </div>
              )}

              {/* ── DONE ── */}
              {stage === "done" && (
                <div className="space-y-4">
                  <div
                    className="flex items-center gap-3 rounded-2xl px-4 py-3"
                    style={{
                      background: "rgba(52,211,153,0.07)",
                      border: "1px solid rgba(52,211,153,0.2)",
                    }}
                  >
                    <CheckCircle2
                      size={16}
                      className="text-emerald-400 flex-shrink-0"
                    />
                    <div>
                      <p className="text-sm font-semibold text-white">
                        Noise removed successfully
                      </p>
                      <p className="text-[10px] text-gray-500 capitalize">
                        {result?.strength} strength · {result?.filename}
                      </p>
                    </div>
                  </div>
                  <AudioPlayerCard
                    label="Original"
                    url={result?.originalUrl}
                    icon={Volume2}
                    color="#6b7280"
                    sublabel="Before processing"
                  />
                  <AudioPlayerCard
                    label="Clean Audio"
                    url={result?.isolatedUrl}
                    icon={Mic}
                    color="#7c3aed"
                    sublabel="Background noise removed"
                  />
                  {result?.isolatedUrl && (
                    <a
                      href={result.isolatedUrl}
                      download
                      className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                      style={{
                        background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                        boxShadow: "0 4px 16px rgba(124,58,237,0.3)",
                      }}
                    >
                      <Download size={14} /> Download Clean Audio
                    </a>
                  )}
                  <button
                    onClick={handleReset}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold text-gray-500 hover:text-gray-300 transition-all"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    <RotateCcw size={12} /> Process another file
                  </button>
                </div>
              )}

              {/* ── ERROR ── */}
              {stage === "error" && (
                <div className="space-y-4">
                  <div
                    className="flex items-start gap-3 rounded-2xl px-4 py-3.5"
                    style={{
                      background: "rgba(239,68,68,0.07)",
                      border: "1px solid rgba(239,68,68,0.2)",
                    }}
                  >
                    <AlertCircle
                      size={16}
                      className="text-red-400 flex-shrink-0 mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-semibold text-white">
                        Processing failed
                      </p>
                      <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                        {error}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleReset}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                    style={{
                      background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                      boxShadow: "0 4px 12px rgba(124,58,237,0.25)",
                    }}
                  >
                    <RotateCcw size={12} /> Try again
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Info strip */}
          {stage === "idle" && (
            <div className="flex items-center justify-center gap-8">
              {[
                ["< 30s", "Processing time"],
                ["200 MB", "Max file size"],
                ["MP3 out", "Output format"],
              ].map(([v, l]) => (
                <div key={l} className="flex flex-col items-center gap-0.5">
                  <span className="text-xs font-bold text-gray-400">{v}</span>
                  <span className="text-[9px] text-gray-700 uppercase tracking-widest">
                    {l}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpeechIsolation;
